import React, { useEffect, useState } from "react";
import { Download, FileSpreadsheet, Loader2, Mail, Pencil, Search, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError, downloadFile } from "../api";
import { fmtDate, useLang } from "../i18n";
import DateInput from "../components/DateInput";

const FORM_ROUTE = {
  dd_persona_natural: "/formularios/persona-natural",
  dd_persona_juridica: "/formularios/persona-juridica",
  declaracion_fondos: "/formularios/declaracion-fondos",
  certificacion_beneficiario: "/formularios/beneficiario-final",
  carta_responsabilidad: "/formularios/carta-responsabilidad",
  dd_pn_aseguradora: "/formularios/dd-pn-aseguradora",
  dd_pj_aseguradora: "/formularios/dd-pj-aseguradora",
};

export default function History() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [filters, setFilters] = useState({ q: "", kind: "all", result: "", form_type: "", from_date: new Date(Date.now() - 7 * 864e5).toISOString().slice(0, 10), to_date: "" });
  const [selected, setSelected] = useState(null);
  const [emailBusy, setEmailBusy] = useState(false);
  const selectedRow = (items || []).find((x) => `${x.kind}-${x.id}` === selected) || null;

  const emailFor = async (item) => {
    setEmailBusy(true);
    try {
      const url = item.kind === "search" ? `/sanctions/searches/${item.id}/email` : item.kind === "pep" ? `/pep/searches/${item.id}/email` : `/forms/${item.id}/email`;
      const { data } = await api.post(url, {});
      toast.success(`${t("search.email_sent")} ${data.to}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setEmailBusy(false);
    }
  };

  const load = async () => {
    setItems(null);
    try {
      const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v && v !== "all"));
      if (showInactive) params.include_inactive = "1";
      const { data } = await api.get("/history", { params });
      setItems(data);
    } catch (e) {
      toast.error(apiError(e));
      setItems([]);
    }
  };

  useEffect(() => { load(); }, [showInactive]);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
  const FORM_TYPES = Object.keys(FORM_ROUTE);
  const FORM_LABEL = {
    dd_persona_natural: "nav.dd_natural", dd_persona_juridica: "nav.dd_juridica",
    declaracion_fondos: "nav.declaracion", certificacion_beneficiario: "nav.certificacion",
    carta_responsabilidad: "nav.carta", dd_pn_aseguradora: "nav.dd_aseguradora",
    dd_pj_aseguradora: "nav.pj_aseguradora",
  };

  const exportExcel = () => {
    const params = new URLSearchParams();
    if (filters.from_date) params.set("from_date", filters.from_date);
    if (filters.to_date) params.set("to_date", filters.to_date);
    downloadFile(`/export/history.xlsx?${params}`, "historial_kycfederal.xlsx");
  };

  const pdfFor = (item) => {
    if (item.kind === "search") return downloadFile(`/sanctions/searches/${item.id}/pdf`, `consulta_${item.number}.pdf`);
    if (item.kind === "pep") return downloadFile(`/pep/searches/${item.id}/pdf`, `consulta_${item.number}.pdf`);
    return downloadFile(`/forms/${item.id}/pdf`, `${item.number}.pdf`);
  };

  const editForm = async (item) => {
    try {
      const { data } = await api.get(`/forms/${item.id}`);
      navigate(FORM_ROUTE[item.form_type], { state: { edit: data } });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleForm = async (item) => {
    try {
      await api.patch(`/forms/${item.id}`, { active: !item.active });
      toast.success(t("u.updated"));
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="history-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">{t("h.title")}</h1>
          <p className="text-sm text-slate-500 mt-1">{t("h.subtitle")}</p>
        </div>
        <button onClick={exportExcel} data-testid="export-excel-button" className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-700 text-white text-sm font-bold hover:bg-emerald-800 transition-colors">
          <FileSpreadsheet className="w-4 h-4" /> {t("common.download_excel")}
        </button>
      </div>

      <div className="ec-card p-4 ec-filter-bar" data-testid="history-filters">
        <div className="sm:col-span-2 lg:col-span-3">
          <label className="ec-label">{t("h.name_title")}</label>
          <input className="ec-input" value={filters.q} onChange={set("q")} placeholder={t("h.q_ph")} data-testid="history-q-input" />
        </div>
        <div className="lg:col-span-2">
          <label className="ec-label">{t("h.kind")}</label>
          <select className="ec-input" value={filters.kind} onChange={set("kind")} data-testid="history-kind-select">
            <option value="all">{t("h.k_all")}</option>
            <option value="searches">{t("h.k_searches")}</option>
            <option value="pep">{t("h.k_pep")}</option>
            <option value="forms">{t("h.k_forms")}</option>
          </select>
        </div>
        <div className="lg:col-span-2">
          <label className="ec-label">{t("common.result")}</label>
          <select className="ec-input" value={filters.result} onChange={set("result")} data-testid="history-result-select">
            <option value="">{t("h.r_all")}</option>
            <option value="match">{t("h.r_match")}</option>
            <option value="no_match">{t("h.r_no")}</option>
          </select>
        </div>
        <div className="lg:col-span-3">
          <label className="ec-label">{t("h.form_type")}</label>
          <select className="ec-input" value={filters.form_type} onChange={set("form_type")} data-testid="history-formtype-select">
            <option value="">{t("common.all")}</option>
            {FORM_TYPES.map((ft) => <option key={ft} value={ft}>{t(FORM_LABEL[ft])}</option>)}
          </select>
        </div>
        <div className="lg:col-span-2">
          <label className="ec-label">{t("h.from")}</label>
          <DateInput value={filters.from_date} onChange={set("from_date")} data-testid="history-from" />
        </div>
        <div className="lg:col-span-2">
          <label className="ec-label">{t("h.to")}</label>
          <DateInput value={filters.to_date} onChange={set("to_date")} data-testid="history-to" />
        </div>
        <button onClick={load} data-testid="history-filter-button" className="ec-filter-btn sm:col-span-2 lg:col-span-1">
          <Search className="w-4 h-4" /> {t("common.search")}
        </button>
        <div className="sm:col-span-2 lg:col-span-10 flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-500 cursor-pointer">
            <input type="checkbox" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} data-testid="history-show-inactive" />
            {t("common.show_inactive")}
          </label>
        </div>
      </div>

      {selectedRow && (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-[#044CBC]/30 bg-blue-50 px-4 py-3" data-testid="history-selected-bar">
          <span className="text-sm font-bold text-slate-800 flex-1 min-w-[200px]">{selectedRow.number} — {selectedRow.title}</span>
          <button onClick={() => pdfFor(selectedRow)} data-testid="history-selected-pdf" className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#044CBC] text-white text-sm font-bold hover:bg-[#033A8C] transition-colors">
            <Download className="w-4 h-4" /> {t("search.pdf")}
          </button>
          <button onClick={() => emailFor(selectedRow)} disabled={emailBusy} data-testid="history-selected-email" className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-[#044CBC] text-[#044CBC] text-sm font-bold hover:bg-blue-100 transition-colors disabled:opacity-60">
            {emailBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} {t("search.send_email")}
          </button>
          <button onClick={() => setSelected(null)} data-testid="history-clear-selection" className="text-slate-400 hover:text-slate-700" title={t("h.clear_sel")}>
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      <div className="ec-card overflow-hidden" data-testid="history-table-card">
        {items === null ? (
          <div className="p-10 text-center text-sm text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{t("common.loading")}</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-400" data-testid="history-empty">{t("h.empty")}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="bg-[#16335F] text-white text-left">
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("h.number")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("h.module")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("h.name_title")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.result")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.user")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide">{t("common.date")}</th>
                  <th className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-right">{t("common.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={`${item.kind}-${item.id}`} onClick={() => setSelected(selected === `${item.kind}-${item.id}` ? null : `${item.kind}-${item.id}`)} className={`border-t border-slate-100 cursor-pointer ${selected === `${item.kind}-${item.id}` ? "bg-blue-50" : "hover:bg-slate-50"} ${item.active === false ? "opacity-50" : ""}`} data-testid={`history-row-${item.number}`}>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600">
                      {item.number}
                      {item.client_id && <div className="text-[10px] text-[#033A8C]">{item.client_id}</div>}
                    </td>
                    <td className="px-4 py-2.5">
                      {item.kind === "search" && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-orange-100 text-[#033A8C]">{t("h.search")}</span>
                      )}
                      {item.kind === "pep" && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-800">{t("h.pep")}</span>
                      )}
                      {item.kind === "form" && (
                        <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-indigo-100 text-indigo-800">{item.form_type_label}</span>
                      )}
                      {item.active === false && (
                        <span className="ml-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-red-50 text-red-600">{t("common.inactive")}</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 font-semibold text-slate-800">{item.title}</td>
                    <td className="px-4 py-2.5">
                      {item.kind !== "form" ? (
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${item.result === "match" ? "bg-red-50 text-red-700" : "bg-emerald-50 text-emerald-700"}`}>
                          {item.result === "match" ? `${t("h.r_match")} (${item.match_count})` : t("h.r_no")}
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">{item.user_name}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{fmtDate(item.created_at)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap space-x-3" onClick={(e) => e.stopPropagation()}>
                      {item.kind === "form" && (
                        <>
                          <button onClick={() => editForm(item)} data-testid={`history-edit-${item.number}`} className="text-[#033A8C] hover:text-[#044CBC] font-semibold text-xs inline-flex items-center gap-1">
                            <Pencil className="w-3.5 h-3.5" /> {t("common.edit")}
                          </button>
                          <button
                            onClick={() => toggleForm(item)}
                            data-testid={`history-toggle-${item.number}`}
                            className={`text-xs font-bold ${item.active !== false ? "text-red-600 hover:text-red-800" : "text-emerald-600 hover:text-emerald-800"}`}
                          >
                            {item.active !== false ? t("common.deactivate") : t("common.activate")}
                          </button>
                        </>
                      )}
                      <button onClick={() => pdfFor(item)} data-testid={`history-pdf-${item.number}`} className="text-[#044CBC] hover:text-[#033A8C] font-semibold text-xs inline-flex items-center gap-1">
                        <Download className="w-3.5 h-3.5" /> PDF
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
